import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import Card from '@/components/ui/Card'

interface PrayerTimesCardProps {
  date: Date
  nextPrayer?: {
    name: string // i18n key: fajr, dhuhr, asr, etc.
    time: string
    countdown: string
  }
}

export default function PrayerTimesCard({ date, nextPrayer }: PrayerTimesCardProps) {
  const { t, i18n } = useTranslation()

  const formatDate = (date: Date) => {
    const day = date.getDate()
    const month = date.toLocaleDateString(i18n.language, { month: 'long' })
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      {/* Current Day Card */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary-100 dark:bg-primary-900/30 rounded-bl-full opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('prayer.today')}</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{formatDate(date)}</p>
          {nextPrayer && (
            <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('prayer.nextPrayerLabel')}</p>
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                {t(`prayer.prayerNames.${nextPrayer.name}`)}
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300">{nextPrayer.time}</p>
              {nextPrayer.countdown && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {t('prayer.within', { count: nextPrayer.countdown })}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Tomorrow Card */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('prayer.tomorrow')}</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(new Date(date.getTime() + 86400000))}</p>
      </Card>
    </div>
  )
}

